/* eslint-disable jsx-a11y/anchor-is-valid */
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Box, Link } from '@alto-avios/alto-ui'
import { UserDetails } from '@channel/shared-header-footer'

import { pushEventToDataLayer } from '~/utils/googleTagManager'
import { useAuthContext } from '~/context/auth'
import { usePlatformTheme } from '~/context/theme'
import { usePlatformRoutes } from '~/context/partner'

import { HeaderProps } from './Header.types'

import styles from './Header.module.scss'

const SharedHeader = dynamic(
  () => import('@channel/shared-header-footer').then((module) => module.Header),
  { ssr: false }
)

const WhitelabelHeader = dynamic(
  () => import('./WhitelabelHeader').then((module) => module.WhitelabelHeader),
  { ssr: false }
)

const GA_NAV_ELEMENT = 'header navigation'

const ORDERS_ROUTE_PREFIX = '/orders'
const ORDER_HISTORY_ROUTE = '/order-history'

const Header = ({
  userDisplayName = '',
  membershipNumber = '',
  aviosBalance = 0,
  householdAviosBalance,
  aviosBalanceError,
  basketItemsCount,
  isLoading,
  skipContentSelector,
}: HeaderProps) => {
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()
  const { isAuthenticated, handleLogin, handleLogout } = useAuthContext()
  const { isAviosTheme } = usePlatformTheme()
  const t = useTranslations('components.header')

  const showHomeLink =
    router.asPath.includes(ORDERS_ROUTE_PREFIX) || router.asPath.includes(ORDER_HISTORY_ROUTE)

  const userDetails: UserDetails | undefined = isAuthenticated
    ? {
        name: userDisplayName,
        membershipNumber,
      }
    : undefined

  if (!isAviosTheme) {
    return (
      <WhitelabelHeader
        userDisplayName={userDisplayName}
        membershipNumber={membershipNumber}
        aviosBalance={aviosBalance}
        basketItemsCount={basketItemsCount}
        isLoading={isLoading}
      />
    )
  }

  return (
    <nav className={styles.headerContainer}>
      <SharedHeader
        auth={{
          loginProps: { onClick: () => handleLogin({ returnToPath: router.asPath }) },
          logoutProps: { onClick: () => handleLogout() },
        }}
        userDetails={isAuthenticated ? userDetails : undefined}
        isLoading={isLoading}
        skipTo={`#${skipContentSelector}`}
        userBalance={aviosBalanceError ? undefined : aviosBalance}
        userBalanceError={aviosBalanceError}
        householdAviosBalance={householdAviosBalance}
      />

      <div className={styles.subHeaderContainer}>
        <Box display="flex" gap="sm" align="center" justifyContent="flex-start">
          {showHomeLink ? (
            <NextLink href={createRoutePath('/')} passHref legacyBehavior>
              <Link
                underline={false}
                iconEndProps={false}
                iconStartProps={{ iconName: 'arrow-left' }}
                onPress={() => {
                  pushEventToDataLayer('navigation', {
                    nav_element: GA_NAV_ELEMENT,
                    nav_type: 'cta',
                    nav_action: `header home link`,
                    menu_level: `0`,
                  })
                }}
              >
                {t('actions.home')}
              </Link>
            </NextLink>
          ) : null}
        </Box>
        <Box display="flex" gap="sm" align="center" justifyContent="flex-end">
          <NextLink href={createRoutePath(ORDER_HISTORY_ROUTE)} passHref legacyBehavior>
            <Link
              underline={false}
              iconEndProps={{ iconName: 'clock-rotate-left' }}
              onPress={() => {
                pushEventToDataLayer('navigation', {
                  nav_element: GA_NAV_ELEMENT,
                  nav_type: 'cta',
                  nav_action: `header order history`,
                  menu_level: `1`,
                })
              }}
            >
              {t('actions.order_history')}
            </Link>
          </NextLink>

          <NextLink
            href={createRoutePath(`${ORDERS_ROUTE_PREFIX}/payment-options`)}
            passHref
            legacyBehavior
          >
            <Link
              underline={false}
              iconEndProps={{
                iconName: 'cart-shopping',
                iconPrefix: basketItemsCount ? 'fas' : 'far',
              }}
              onPress={() => {
                pushEventToDataLayer('navigation', {
                  nav_element: GA_NAV_ELEMENT,
                  nav_type: 'cta',
                  nav_action: `header payment options`,
                  menu_level: `2`,
                })
              }}
            >
              {t('actions.basket')} {basketItemsCount ? ` (${basketItemsCount})` : ''}
            </Link>
          </NextLink>
        </Box>
      </div>
    </nav>
  )
}

export default Header
